import {Grid, MenuItem, TextField, Typography, CircularProgress} from "@mui/material"
import {useEffect, useState} from "react"

import ApplicationItem from "../../../components/cards/listings/applicationItem";
import MyApplicationItem from "../../../components/cards/listings/myApplicationItem";
import { ResponsiveRow } from "../../../constants/layout.constants";
import {
  listApplications,
  listMyApplications,
  deleteListingApplication,
} from "../../../services/listingsServices/listing.service";
import ViewApplication from "../../../components/cards/listings/viewApplication";
import ListingApplicationModal from "../../../components/modals/listings/listingApplicationModal";
import ConfirmationModal from "../../../components/modals/confirmation";
import {
  issueWarnMessage,
  issueSuccessMessage,
} from "../../../actions/message";

export default function ListingApplications({ onPendingCountUpdate }) {
    const [applications, setApplications] = useState([])
    const [myApplications, setMyApplications] = useState([])
    const [applicationToView, setApplicationToView] = useState(null)
    const [viewModal, setViewModal] = useState(false)
    const [applicationToEdit, setApplicationToEdit] = useState(null)
    const [editModal, setEditModal] = useState(false)
    const [applicationToDelete, setApplicationToDelete] = useState(null)
    const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
    const [listing, setListing] = useState('')
    const [sortBy, setSortBy] = useState('')
    const [selectedStatus, setSelectedStatus] = useState('')
    const [myApplicationsStatus, setMyApplicationsStatus] = useState('')
    const [subscriptionError, setSubscriptionError] = useState(null)
    const [applicationType, setApplicationType] = useState("myListings")
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (applicationType === "myListings") {
            // Load applications to my listings
            setLoading(true)
            listApplications(listing, selectedStatus)
                .then((data) => {
                    // Check if the response indicates a subscription error
                    if (data && data.type === "error" && data.requiresSubscription) {
                        setSubscriptionError({
                            title: "SmartKodi Subscription Required",
                            body: data.message || "This feature requires a SmartKodi subscription."
                        })
                        // Set applications from error response for blurred display
                        if (data.applications) {
                            setApplications(Array.isArray(data.applications) ? data.applications : [])
                        } else {
                            setApplications([])
                        }
                        return
                    }
                    
                    // If it's a success response with applications, extract the applications array
                    if (data && data.type === "success" && data.applications) {
                        setApplications(Array.isArray(data.applications) ? data.applications : [])
                        setSubscriptionError(null)
                    } else {
                        // Handle case where data might be the applications array directly
                        setApplications(Array.isArray(data) ? data : [])
                        setSubscriptionError(null)
                    }
                })
                .catch((error) => {
                    console.error("listApplications error", error);
                    // Set empty array on error to prevent non-iterable issues
                    setApplications([])
                    setSubscriptionError(null)
                })
                .finally(() => {
                    setLoading(false)
                })
        } else {
            // Load my submitted applications
            listMyApplications(myApplicationsStatus)
                .then((data) => {
                    setMyApplications(Array.isArray(data) ? data : [])
                })
                .catch((error) => {
                    console.error("listMyApplications error", error);
                    setMyApplications([])
                })
        }
    }, [listing, selectedStatus, myApplicationsStatus, applicationType])

  function viewApplicationItem(item) {
    setViewModal(true);
    setApplicationToView(item);
  }

  function editApplicationItem(item) {
    setApplicationToEdit(item);
    setEditModal(true);
  }

  function deleteApplicationItem(item) {
    setApplicationToDelete(item);
    setDeleteConfirmationModal(true);
  }

  function handleDeleteConfirm() {
    if (applicationToDelete) {
      deleteListingApplication(applicationToDelete.id)
        .then((response) => {
          if (response.type === "success") {
            // Remove the application from the local state
            if (applicationType === "myListings") {
              setApplications(
                applications.filter((app) => app.id !== applicationToDelete.id),
              );
            } else {
              setMyApplications(
                myApplications.filter(
                  (app) => app.id !== applicationToDelete.id,
                ),
              );
            }
            issueSuccessMessage(response.message);
          } else {
            issueWarnMessage(
              response.message || "Failed to delete application",
            );
          }
        })
        .catch((error) => {
          console.error("Delete application error:", error);
          issueWarnMessage("Failed to delete application. Please try again.");
        })
        .finally(() => {
          setDeleteConfirmationModal(false);
          setApplicationToDelete(null);
        });
    }
  }

  function handleDeleteCancel() {
    setDeleteConfirmationModal(false);
    setApplicationToDelete(null);
  }

  function handleCancel() {
    setViewModal(false);
    setApplicationToView(null);
  }

  function handleEditCancel() {
    setEditModal(false);
    setApplicationToEdit(null);
  }

  function handleApplicationTypeChange(event) {
    setApplicationType(event.target.value);
  }

  function handleSortBy(event) {
    const value = event.target.value;
    setSortBy(value);
  }

  // Reusable sorting function for applications
  const sortApplications = (appList) => {
    return [...appList].sort((a, b) => {
      switch (sortBy) {
        case "listingAsc":
          return (a.listing?.propertyName || "").localeCompare(
            b.listing?.propertyName || "",
          );
        case "listingDesc":
          return (b.listing?.propertyName || "").localeCompare(
            a.listing?.propertyName || "",
          );
        case "appNameAsc":
          return (a.applicant?.name || "")
            .toLowerCase()
            .localeCompare((b.applicant?.name || "").toLowerCase());
        case "appNameDesc":
          return (b.applicant?.name || "")
            .toLowerCase()
            .localeCompare((a.applicant?.name || "").toLowerCase());
        case "dateAsc":
          return new Date(a.dateCreated) - new Date(b.dateCreated);
        case "dateDesc":
          return new Date(b.dateCreated) - new Date(a.dateCreated);
        default:
          return 0;
      }
    });
  };

  // Create sorted versions using the reusable function
  const sortedApplications = sortApplications(applications);
  const sortedMyApplications = sortApplications(myApplications);

  function filterByStatus(event) {
    const value = event.target.value;
    if (value === "PEN") {
      setSelectedStatus("PEN");
    } else if (value === "APP") {
      setSelectedStatus("APP");
    } else if (value === "REJ") {
      setSelectedStatus("REJ");
    } else {
      setSelectedStatus("NEW");
    }
  }

  function filterMyApplicationsByStatus(event) {
    const value = event.target.value;
    setMyApplicationsStatus(value);
  }

  const handleUpgradeToPremium = () => {
    window.open("/signUp", "_blank");
  };

  const handleContactSupport = () => {
    window.open("/faq", "_blank");
  };

  const handleApplicationUpdated = () => {
    // Refresh both applications lists
    if (applicationType === "myListings") {
      // Refresh applications to my listings
      listApplications(listing, selectedStatus)
        .then((data) => {
          setApplications(Array.isArray(data) ? data : []);
          // Update the pending count in the navbar
          if (onPendingCountUpdate) {
            onPendingCountUpdate();
          }
        })
        .catch((error) => {
          console.error("listApplications error", error);
          setApplications([]);
        });
    } else {
      // Refresh my applications
      listMyApplications(myApplicationsStatus)
        .then((data) => {
          setMyApplications(Array.isArray(data) ? data : []);
        })
        .catch((error) => {
          console.error("listMyApplications error", error);
          setMyApplications([]);
        });
    }
  };

  return (
    <ResponsiveRow
      sx={{ flexDirection: "column", width: "100%", alignItems: "center" }}
    >
      {/* If there's a subscription error, apply blur to the entire content */}
      <div
        style={{
          filter: subscriptionError ? "blur(4px)" : "none",
          opacity: subscriptionError ? 0.4 : 1,
          pointerEvents: subscriptionError ? "none" : "auto",
          transition: "all 0.3s ease",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <ResponsiveRow
          sx={{
            width: { xs: "100%", lg: "80%" },
            padding: "10px auto",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ width: "100%", textAlign: "start" }}>
            {applicationType === "myListings"
              ? "Applications to My Listings"
              : "My Applications"}
          </Typography>
        </ResponsiveRow>

        <ResponsiveRow
          sx={{
            marginTop: "10px",
            width: { xs: "100%", lg: "80%" },
            padding: 0,
            justifyContent: { xs: "flex-start", lg: "space-between" },
            alignItems: "center",
            flexWrap: { xs: "wrap", lg: "nowrap" },
          }}
        >
          <Grid
            item
            sx={{
              width: { xs: "100%", lg: "auto" },
              textAlign: "start",
            }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "250px" },
                backgroundColor: "#fff",
                borderRadius: "10px",
                textAlign: "start",
                margin: "5px 0",
              }}
              label="Application Type"
              select
              value={applicationType}
              onChange={handleApplicationTypeChange}
            >
              <MenuItem value={"myListings"}>
                Applications to My Listings
              </MenuItem>
              <MenuItem value={"myApplications"}>My Applications</MenuItem>
            </TextField>
          </Grid>

          {/* Search By Listing Name - Always shown */}
          <Grid
            item
            sx={{
              width: { xs: "100%", lg: "50%" },
              textAlign: "center",
            }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "400px" },
                backgroundColor: "#fff",
                borderRadius: "10px",
                textAlign: "start",
                margin: "5px 0",
              }}
              label="Search By Listing Name"
              value={listing}
              onChange={(event) => {
                const value = event.target.value;
                setListing(value);
              }}
            />
          </Grid>

          {/* Sort and Filter Controls */}
          <Grid
            item
            sx={{
              flexDisplay: "row",
              justifyContent: { xs: "center", lg: "flex-end" },
              width: { xs: "100%", lg: "50%" },
              textAlign: "end",
            }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "200px" },
                backgroundColor: "#fff",
                borderRadius: "10px",
                textAlign: "start",
                margin: "5px 0",
              }}
              label="Sort By"
              select
              value={sortBy}
              onChange={handleSortBy}
            >
              {applicationType === "myListings"
                ? [
                    <MenuItem key="appNameAsc" value={"appNameAsc"}>
                      Applicant's Name (Asc)
                    </MenuItem>,
                    <MenuItem key="appNameDesc" value={"appNameDesc"}>
                      Applicant's Name (Desc)
                    </MenuItem>,
                    <MenuItem key="listingAsc" value={"listingAsc"}>
                      Listing Name (Asc)
                    </MenuItem>,
                    <MenuItem key="listingDesc" value={"listingDesc"}>
                      Listing Name (Desc)
                    </MenuItem>,
                    <MenuItem key="dateAsc" value={"dateAsc"}>
                      Date (Asc)
                    </MenuItem>,
                    <MenuItem key="dateDesc" value={"dateDesc"}>
                      Date (Desc)
                    </MenuItem>,
                  ]
                : [
                    <MenuItem key="listingAsc" value={"listingAsc"}>
                      Property Name (Asc)
                    </MenuItem>,
                    <MenuItem key="listingDesc" value={"listingDesc"}>
                      Property Name (Desc)
                    </MenuItem>,
                    <MenuItem key="dateAsc" value={"dateAsc"}>
                      Date (Asc)
                    </MenuItem>,
                    <MenuItem key="dateDesc" value={"dateDesc"}>
                      Date (Desc)
                    </MenuItem>,
                  ]}
            </TextField>

            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "200px" },
                backgroundColor: "#fff",
                borderRadius: "10px",
                textAlign: "start",
                margin: { xs: "5px 0", lg: "5px" },
              }}
              label="Filter By Status"
              select
              value={
                applicationType === "myListings"
                  ? selectedStatus
                  : myApplicationsStatus
              }
              onChange={
                applicationType === "myListings"
                  ? filterByStatus
                  : filterMyApplicationsByStatus
              }
            >
              {applicationType === "myListings"
                ? [
                    <MenuItem key="PEN" value={"PEN"}>
                      Pending
                    </MenuItem>,
                    <MenuItem key="APP" value={"APP"}>
                      Approved
                    </MenuItem>,
                    <MenuItem key="REJ" value={"REJ"}>
                      Rejected
                    </MenuItem>,
                  ]
                : [
                    <MenuItem key="ALL" value={""}>
                      All
                    </MenuItem>,
                    <MenuItem key="PEN" value={"PEN"}>
                      Pending
                    </MenuItem>,
                    <MenuItem key="APP" value={"APP"}>
                      Approved
                    </MenuItem>,
                    <MenuItem key="REJ" value={"REJ"}>
                      Rejected
                    </MenuItem>,
                  ]}
            </TextField>
          </Grid>
        </ResponsiveRow>
        <ResponsiveRow
          sx={{
            justifyContent: "center",
            alignItems: "center",
            height: "fit-content",
            width: { xs: "100%", lg: "80%" },
            padding: "0",
            marginTop: "20px",
          }}
        >
          {applicationType === "myListings" ? (
            // Applications to My Listings
            sortedApplications && sortedApplications.length > 0 ? (
              sortedApplications.map((item, i) => {
                return (
                  <ApplicationItem
                    key={i}
                    applicationItem={item}
                    viewApplicationItem={viewApplicationItem}
                  />
                );
              })
            ) : (
              <Typography
                variant="h6"
                sx={{ color: "#666", marginTop: "40px" }}
              >
                No applications found for your listings
              </Typography>
            )
          ) : // My Applications
          sortedMyApplications && sortedMyApplications.length > 0 ? (
            sortedMyApplications.map((item, i) => {
              return (
                <MyApplicationItem
                  key={i}
                  applicationItem={item}
                  viewApplicationItem={viewApplicationItem}
                  editApplicationItem={editApplicationItem}
                  deleteApplicationItem={deleteApplicationItem}
                />
              );
            })
          ) : (
            <Typography variant="h6" sx={{ color: "#666", marginTop: "40px" }}>
              You haven't submitted any applications yet
            </Typography>
          )}
        </ResponsiveRow>
      </div>

      {/* Subscription error overlay */}
      {subscriptionError && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translateX(-50%) translateY(-50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              borderRadius: "15px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
              maxWidth: "500px",
              width: "90%",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              transform: "translateY(50px)",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                backgroundColor: "#037171",
                borderRadius: "50%",
                margin: "0 auto 20px auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  fontSize: "40px",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                🔒
              </span>
            </div>

            <h2
              style={{
                color: "#037171",
                marginBottom: "15px",
                fontSize: "28px",
                fontWeight: "700",
              }}
            >
              Unlock Premium Features
            </h2>

            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                color: "#666",
                marginBottom: "25px",
              }}
            >
              Get access to powerful listing management tools and advanced
              features with SmartKodi Premium.
            </p>

            <div
              style={{
                backgroundColor: "#f8f9fa",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "25px",
                border: "1px solid #e9ecef",
              }}
            >
              <h4
                style={{
                  color: "#037171",
                  margin: "0 0 10px 0",
                  fontSize: "18px",
                }}
              >
                What you'll get:
              </h4>
              <ul
                style={{
                  textAlign: "left",
                  margin: "0",
                  paddingLeft: "20px",
                  color: "#666",
                }}
              >
                <li>Advanced listing management</li>
                <li>Application processing tools</li>
                <li>Analytics and reporting</li>
                <li>Priority support</li>
              </ul>
            </div>

            <button
              style={{
                backgroundColor: "#037171",
                color: "white",
                border: "none",
                padding: "12px 30px",
                borderRadius: "25px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 15px rgba(3, 113, 113, 0.3)",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#025a5a";
                e.target.style.transform = "translateY(-2px)";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "#037171";
                e.target.style.transform = "translateY(0)";
              }}
              onClick={handleUpgradeToPremium}
            >
              Subscribe to Smartkodi
            </button>

            <p
              style={{
                fontSize: "14px",
                color: "#999",
                marginTop: "15px",
                marginBottom: "0",
              }}
            >
              Already have a subscription?{" "}
              <a
                href="#"
                style={{
                  color: "#037171",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleContactSupport();
                }}
              >
                Contact support
              </a>
            </p>
          </div>
        </div>
      )}

      {viewModal && (
        <ViewApplication
          applicationItem={applicationToView}
          openViewModal={viewModal}
          closeViewModal={handleCancel}
          onApplicationUpdated={handleApplicationUpdated}
          isMyApplication={applicationType === "myApplications"}
        />
      )}

      {editModal && applicationToEdit && (
        <ListingApplicationModal
          applicationItem={applicationToEdit}
          openModal={editModal}
          closeModal={handleEditCancel}
          onApplicationUpdated={handleApplicationUpdated}
        />
      )}

      {deleteConfirmationModal && applicationToDelete && (
        <ConfirmationModal
          showConfirmationModal={deleteConfirmationModal}
          handleConfirmationClose={handleDeleteCancel}
          title="Delete Application"
          severity="warning"
          body={`Are you sure you want to delete your application for "${applicationToDelete.listing?.propertyName}"? This action cannot be undone.`}
          positiveText="Delete"
          negativeText="Cancel"
          positiveAction={handleDeleteConfirm}
          negativeAction={handleDeleteCancel}
        />
      )}
    </ResponsiveRow>
  );
}
